<template>
    <div class="col-md-12 d-flex justify-content-start">
        <SearchInput 
            placeholder="Buscar por nome" 
            @search="handleSearch" 
            :isSearch="isSearch"
            @keydown.enter.prevent="handleSearch" 
        />
    </div>
    <Table :headers="headers" :items="listHistory" />
    <div class="col-md-12 d-flex justify-content-end">
        <Pagination :numberPage="numberPage" :currentPage="page" />
    </div>

    <ImportFailure v-if="modalFailure" :arrError="errorArr" />
</template>

<script>
import Table from "@/components/Custom/Table.vue";
import ImportHistoryService from '@/services/importHistory-service';
import Pagination from "@/components/Pagination/Pagination.vue";
import ImportFailure from "@/components/Import/ImportFailure.vue";
import SearchInput from "@/components/Custom/SearchInput.vue";
import {
    generateMessage,
    getMessagesToTranslatePlugin,
    handleErrorMessage,
    translateArray,
} from "@/utils/utils";

export default {
    name: "ImportHistory",
    components: {
        Table,
        Pagination,
        ImportFailure,
        SearchInput,
    },
    props: {
        resource: { type: String, required: true },
        pluralName: { type: String, required: true },
        options: { type: Object, default: () => ({}) }
    },
    data() {
        return {
            headers: [
                {
                    text: "Nome do arquivo",
                    value: "name",
                    tooltip: true,
                },
                {
                    text: "Data da importação",
                    value: "createdAt",
                    type: "date-iso"
                },
                {
                    text: "Status",
                    value: "status",
                    type: "icon"
                },
                {
                    text: "Ações",
                    value: "acoes",
                    align: "right",
                    type: "action",
                },
            ],
            listHistory: [],
            page: parseInt(this.$route.query.page) || 1,
            limit: parseInt(this.$route.query.limit) || 5,
            search: this.$route.query.name || "",
            numberPage: 0,
            isSearch: false,
            errorArr: [],
            modalFailure: false,
        };
    },
    mounted() {
        this.getHistory();
    },
    watch: {
        $route(to) {
            this.page = parseInt(to.query.page) || 1;
            this.limit = parseInt(to.query.limit) || 5;
            this.search = to.query.name || "";
            this.getHistory();
            this.isSearch = false;
            if (this.search) this.isSearch = true;
        },
        options() {
            this.getHistory();
        }
    },
    methods: {
        async getHistory() {
            const response = await ImportHistoryService.getAll(this.resource, {
                page: this.page,
                limit: this.limit,
                name: this.search,
                ...this.options
            });
            this.numberPage = response?.totalPage || 0;
            const data = response?.results || [];
            this.listHistory = data.map(item => {
                return {
                    id: item._id,
                    name: item.name,
                    createdAt: item.createdAt,
                    status: this.getStatusIcon(item.status),
                    details: item.details,
                    acoes: this.getActions(item),
                };
            });
        },
        getStatusIcon(status) {
            let classIcon = "";
            let color = '';
            let label = '';
            switch (status) {
                case "success":
                    classIcon =
                        "fa-regular fa-circle-check";
                    color = 'var(--color-success)';
                    label = 'Sucesso';
                    break;
                case "pending":
                    classIcon =
                        "fa-regular fa-clock";
                    color = 'var(--color-primary)';
                    label = 'Em fila';
                    break;
                case "fatal":
                    classIcon =
                        "fa-regular fa-circle-xmark";
                    color = 'var(--color-danger)';
                    label = 'Falha';
                    break;
                case "in-progress":
                    classIcon =
                        "fa-regular fa-rotate-left";
                    color = 'var(--color-info)';
                    label = 'Processando';
                    break;
                case "failure":
                    classIcon = "fa-regular fa-ban";
                    color = 'var(--color-warning)';
                    label = 'Falhou';
                    break;
                case "partial":
                    classIcon = "fa-regular fa-ban";
                    color = 'var(--color-warning)';
                    label = 'Falha parcialmente';
                    break;
                default:
                    classIcon = "fa-regular fa-circle-check";
                    color = 'var(--color-success)';
                    label = 'Em fila';
                    break;
            }
            return {
                "text": label,
                "icon": classIcon,
                "color": color
            };
        },
        getActions(item) {
            let actions = [{
                color: "var(--color-primary)",
                title: "Baixar CSV original",
                icon: "fa-regular fa-arrow-down-to-line",
                onClick: (item) => {
                    this.downloadFileCsv(item.id);
                },
            }];
            switch (item.status) {
                case 'partial':
                case 'failure':
                    actions.unshift({
                        color: "var(--color-danger)",
                        title: "Falha na importação do arquivo. Clique para visualizar.",
                        icon: "fa-regular fa-circle-exclamation",
                        onClick: (item) => {
                            this.openModalFailure(item.details);
                        },
                    });
                    break;
                case 'fatal':
                    actions.unshift({
                        color: "var(--color-danger)",
                        title: "Baixar erros de importação",
                        icon: "fa-regular fa-triangle-exclamation",
                        onClick: (item) => {
                            this.downloadResultFileCsv(item.id);
                        },
                    })
                    break;
            }
            return actions;
        },
        openModalFailure(errors) {
            this.errorArr = errors;
            this.modalFailure = !this.modalFailure;
        },
        async downloadResultFileCsv(id) {
            try {
                await ImportHistoryService.downloadCsvResult(this.resource, `Erros de importação de ${this.pluralName}`, id);
            } catch (error) {
                var arrKeys = getMessagesToTranslatePlugin(handleErrorMessage(error));
                var arrTranslates = translateArray(arrKeys, this);
                generateMessage(arrTranslates, "ERROR", this);
            }
        },
        async downloadFileCsv(id) {
            try {
                await ImportHistoryService.downloadCsvImport(this.resource, `Arquivo de importação de ${this.pluralName}`, id);
            } catch (error) {
                var arrKeys = getMessagesToTranslatePlugin(handleErrorMessage(error));
                var arrTranslates = translateArray(arrKeys, this);
                generateMessage(arrTranslates, "ERROR", this);
            }
        },
        handleSearch(search) {
            this.$router.replace({
                name: this.$router.currentRoute.name,
                query: { page: 1, limit: this.limit, name: search },
            });
        },
    }
};
</script>